/**
 * Template catalog (Deliverable E) — the starting point for adding a new language.
 *
 * This is the heart of the "add your language" path: a complete scaffold in which EVERY
 * canonical `MessageId` is already present with a clear, visible placeholder
 * (`«TODO: <name>»`), and the ICU arguments each message takes are shown inline (e.g.
 * `{w}`, `{kind}`, `{count}`, `{constraints}`) so a contributor can see exactly what data
 * the string receives. Adding a language is then a pure catalog drop-in (zero core code,
 * INV-STABLE-ID): copy this file to `catalogs/<locale>.ts`, set `locale` + `dir`, translate
 * each placeholder, and register it — `font-stacks.ts`/`scriptOf` already cover the script.
 *
 * Because the placeholders are themselves valid (plain-text) ICU patterns, the template
 * renders without throwing, and a *partial* translation (some keys still `«TODO»`, or even
 * absent) degrades gracefully through the provider's fallback locale. See
 * `docs/adding-a-language.md` for the contributor playbook and the parity check.
 *
 * The set of keys here is generated from `EN_CATALOG` (the canonical key set), so it stays
 * in lock-step with what the L4 view-model emits and is enforced by `parity.test.ts`.
 */
import { P2 } from '../message-ids-phase2.js';
/** The marker that flags an untranslated entry. A contributor replaces the whole value. */
export const TEMPLATE_PLACEHOLDER = '«TODO:';
/**
 * The starter catalog: every canonical key present, each value an obvious placeholder
 * carrying the message's ICU argument names. `locale: 'xx'` (private-use) + `dir: 'ltr'`
 * are placeholders too — a contributor sets the real BCP-47 locale and direction.
 */
export const TEMPLATE_CATALOG = {
    locale: 'xx',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: "«TODO: inquiryTitle»",
        [P2.sectionClaims]: "«TODO: sectionClaims»",
        [P2.sectionIntegrity]: "«TODO: sectionIntegrity»",
        [P2.axisWarrantLabel]: "«TODO: axisWarrantLabel»",
        [P2.axisWarrantValue]: "«TODO: axisWarrantValue» {w}",
        [P2.axisEmpiricalLabel]: "«TODO: axisEmpiricalLabel»",
        [P2.axisEmpiricalValue]: "«TODO: axisEmpiricalValue» {kind}",
        [P2.axisModalLabel]: "«TODO: axisModalLabel»",
        [P2.axisModalValue]: "«TODO: axisModalValue» {m}",
        [P2.axisKidSummary]: "«TODO: axisKidSummary» {w}",
        [P2.stateExplorable]: "«TODO: stateExplorable»",
        [P2.stateRefuted]: "«TODO: stateRefuted»",
        [P2.stateOutside]: "«TODO: stateOutside»",
        [P2.verdictConflict]: "«TODO: verdictConflict» {constraints}",
        [P2.verdictOutside]: "«TODO: verdictOutside» {missing}",
        [P2.lossRegisterPlain]: "«TODO: lossRegisterPlain»",
        [P2.lossRegisterKid]: "«TODO: lossRegisterKid»",
        [P2.lossProfileDetail]: "«TODO: lossProfileDetail»",
        [P2.lossConflictDetail]: "«TODO: lossConflictDetail»",
        [P2.laneVerified]: "«TODO: laneVerified»",
        [P2.laneUnverified]: "«TODO: laneUnverified»",
        [P2.laneConflict]: "«TODO: laneConflict»",
        [P2.constraintLogic]: "«TODO: constraintLogic»",
        [P2.constraintLightBends]: "«TODO: constraintLightBends»",
        [P2.constraintParallelPostulate]: "«TODO: constraintParallelPostulate»",
        [P2.claimScalarGravity]: "«TODO: claimScalarGravity»",
        [P2.claimNonEuclidean]: "«TODO: claimNonEuclidean»",
        [P2.claimSubstrateSr]: "«TODO: claimSubstrateSr»",
        [P2.edgeExtObservation]: "«TODO: edgeExtObservation»",
        [P2.edgeResourceSample]: "«TODO: edgeResourceSample»",
        [P2.edgeHeuristicSample]: "«TODO: edgeHeuristicSample»",
        [P2.edgeSharedSample]: "«TODO: edgeSharedSample»",
        [P2.sectionConstraints]: "«TODO: sectionConstraints»",
        [P2.sectionEdges]: "«TODO: sectionEdges»",
        [P2.sectionFrame]: "«TODO: sectionFrame»",
        [P2.sectionExposure]: "«TODO: sectionExposure»",
        [P2.statusAdoptedFormal]: "«TODO: statusAdoptedFormal»",
        [P2.statusAdoptedInterpretive]: "«TODO: statusAdoptedInterpretive»",
        [P2.statusSuspended]: "«TODO: statusSuspended»",
        [P2.edgeEffectConstraint]: "«TODO: edgeEffectConstraint»",
        [P2.edgeEffectResource]: "«TODO: edgeEffectResource»",
        [P2.edgeEffectHeuristic]: "«TODO: edgeEffectHeuristic»",
        [P2.edgeEffectShared]: "«TODO: edgeEffectShared»",
        // `frameActive` is an ICU plural — your locale's CLDR categories go here, e.g.
        // `{count, plural, one {…} other {…}}`. See docs/adding-a-language.md (pluralization).
        [P2.frameActive]: "«TODO: frameActive» {count}",
        [P2.exposureNone]: "«TODO: exposureNone»",
        // `{id}` is a free-standing technical id — in an RTL language wrap it LRI…PDI to keep it
        // left-to-right (see the Arabic catalog `exposureDep` for the pattern).
        [P2.exposureDep]: "«TODO: exposureDep» {id}",
        [P2.laneTrackMachine]: "«TODO: laneTrackMachine»",
        [P2.laneTrackExpert]: "«TODO: laneTrackExpert»",
        [P2.laneTrackCommunity]: "«TODO: laneTrackCommunity»",
        [P2.expertAudited]: "«TODO: expertAudited»",
        [P2.expertInProgress]: "«TODO: expertInProgress»",
        [P2.expertNotReviewed]: "«TODO: expertNotReviewed»",
        [P2.communityFlags]: "«TODO: communityFlags»",
        [P2.communityNone]: "«TODO: communityNone»",
        [P2.controlChallenge]: "«TODO: controlChallenge»",
        [P2.controlActivate]: "«TODO: controlActivate»",
        [P2.controlMakeInterpretive]: "«TODO: controlMakeInterpretive»",
        [P2.controlImportEdge]: "«TODO: controlImportEdge»",
        [P2.controlPromote]: "«TODO: controlPromote»",
        [P2.controlUndo]: "«TODO: controlUndo»",
        [P2.controlRedo]: "«TODO: controlRedo»",
        [P2.controlRegister]: "«TODO: controlRegister»",
        [P2.promoteRejected]: "«TODO: promoteRejected»",
        [P2.promoteAccepted]: "«TODO: promoteAccepted»",
        [P2.promoteReasonRefutationTerminal]: "«TODO: promoteReasonRefutationTerminal»",
        [P2.promoteReasonKeystone]: "«TODO: promoteReasonKeystone»",
        [P2.promoteReasonEmpiricalNotFixed]: "«TODO: promoteReasonEmpiricalNotFixed»",
        [P2.promoteReasonModalNotFixed]: "«TODO: promoteReasonModalNotFixed»",
        [P2.promoteReasonWarrantNotImproved]: "«TODO: promoteReasonWarrantNotImproved»",
        // Compute-integration keys. `{result}` is pre-formatted per locale by `formatResult`;
        // `{dp}` is a plain number (NOT a plural agreement). In an RTL language, LRI…PDI-wrap
        // `{result}` (an LTR math expression) — see the Arabic catalog for the pattern.
        [P2.evidenceComputation]: "«TODO: evidenceComputation» {engine} {op} {result} {config}",
        [P2.lossNumericRounding]: "«TODO: lossNumericRounding» {dp}",
    },
};
/**
 * The canonical key set every catalog must cover (the template's own keys). A parity check
 * compares a candidate catalog against this and reports exactly which ids remain.
 */
export const CANONICAL_KEYS = Object.freeze(Object.keys(TEMPLATE_CATALOG.messages).sort());
export function parityOf(catalog, canonical = CANONICAL_KEYS) {
    const present = catalog.messages;
    const canonicalSet = new Set(canonical);
    const missing = canonical.filter((k) => !(k in present));
    const untranslated = canonical.filter((k) => typeof present[k] === 'string' && present[k].startsWith(TEMPLATE_PLACEHOLDER));
    const extra = Object.keys(present).filter((k) => !canonicalSet.has(k));
    return {
        missing,
        untranslated,
        extra,
        complete: missing.length === 0 && untranslated.length === 0,
    };
}
//# sourceMappingURL=template.js.map